import { BASE, request } from "./client";
import type { SessionDetail } from "./sessions";
import type {
  TechMetrics,
  DailyTrendItem,
  RejectBreakdownItem,
  PlaybookDistributionItem,
} from "./analytics";

function _tenantQs(tenantId?: string): string {
  return tenantId ? `?tenant_id=${encodeURIComponent(tenantId)}` : "";
}

export interface ApiKeyRecord {
  id: string;
  tenant_id: string;
  name: string;
  note: string;
  created_at: number;
  active: number;
  last_used_at: number | null;
  total_calls: number;
}

export interface UsageLog {
  id: number;
  key_id: string;
  endpoint: string;
  call_id: string | null;
  created_at: number;
  status_code: number;
}

export function listApiKeys(tenantId?: string): Promise<{ keys: ApiKeyRecord[] }> {
  return request(`/admin/api-keys${_tenantQs(tenantId)}`);
}

export function createApiKey(
  name: string,
  note: string,
  tenantId: string,
): Promise<{ id: string; key: string; tenant_id: string }> {
  return request("/admin/api-keys", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ name, note, tenant_id: tenantId }),
  });
}

export function revokeApiKey(id: string): Promise<{ id: string; revoked: boolean }> {
  return request(`/admin/api-keys/${id}`, { method: "DELETE" });
}

export function getApiKeyUsage(id: string, limit = 50, offset = 0): Promise<{ logs: UsageLog[] }> {
  return request(`/admin/api-keys/${id}/usage?${new URLSearchParams({ limit: String(limit), offset: String(offset) })}`);
}

export interface AdminSessionDetail extends SessionDetail {
  tenant_id: string;
}

export function adminGetSessionDetail(id: string): Promise<AdminSessionDetail> {
  return request(`/admin/sessions/${id}`);
}

export function adminSessionAudioUrl(sessionId: string, turnIndex: number): string {
  return `${BASE}/admin/sessions/${sessionId}/audio/${turnIndex}`;
}

export function adminSessionRecordingUrl(
  sessionId: string,
  track?: "user" | "ai",
): string {
  const base = `${BASE}/admin/sessions/${sessionId}/recording`;
  return track ? `${base}?track=${track}` : base;
}

export function adminDumpSession(id: string): Promise<Record<string, unknown>> {
  return request(`/admin/sessions/${id}/dump`);
}

export interface Tenant {
  id: string;
  name: string;
  daily_call_limit: number;
  created_at: number;
}

export interface TenantUser {
  id: string;
  username: string;
  role: "owner" | "editor" | "viewer";
  is_platform_admin: boolean;
  created_at: number;
}

export interface AdminPlaybookRecord {
  id: string;
  tenant_id: string;
  display_name: string;
  voice_id: string;
  app: string;
  created_at: number;
  updated_at: number;
}

export interface AdminVoiceRecord {
  id: string;
  tenant_id: string;
  display_name: string;
  role: string;
  created_at: number;
}

export interface AdminGatewayRecord {
  id: string;
  tenant_id: string;
  name: string;
  dial_string: string;
  caller_ids: string[];
  caller_id_prefix: string;
  enabled: boolean;
  weight: number;
  daily_limit: number;
  created_at: number;
}

export interface AdminAgentBackendRecord {
  id: string;
  tenant_id: string;
  display_name: string;
  endpoint: string;
  slug: string;
  api_key: string;
  voice_id: string;
  created_at: number;
  updated_at: number;
}

export interface AdminApiKeyRecord extends ApiKeyRecord {
  tenant_id: string;
}

export type TransferResource =
  | "playbooks"
  | "voices"
  | "gateways"
  | "agent-backends"
  | "api-keys";

export type TransferAffected = Record<string, number>;

export type TransferResponse =
  | { requires_confirm: true; affected: TransferAffected }
  | { ok: true };

export function adminListTenants(): Promise<Tenant[]> {
  return request("/admin/tenants");
}

export function adminCreateTenant(
  name: string,
  ownerUsername: string,
  ownerPassword: string,
): Promise<{ tenant_id: string }> {
  return request("/admin/tenants", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      name,
      owner_username: ownerUsername,
      owner_password: ownerPassword,
    }),
  });
}

export function adminListTenantUsers(tenantId: string): Promise<TenantUser[]> {
  return request(`/admin/tenants/${encodeURIComponent(tenantId)}/users`);
}

export function adminUpdateTenantDailyCallLimit(
  tenantId: string,
  limit: number,
): Promise<{ ok: true; daily_call_limit: number }> {
  return request(`/admin/tenants/${encodeURIComponent(tenantId)}/daily-call-limit`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ daily_call_limit: limit }),
  });
}

export function adminListPlaybooks(tenantId?: string): Promise<AdminPlaybookRecord[]> {
  return request(`/admin/playbooks${_tenantQs(tenantId)}`);
}

export function adminListVoices(tenantId?: string): Promise<AdminVoiceRecord[]> {
  return request(`/admin/voices${_tenantQs(tenantId)}`);
}

export function adminListGateways(tenantId?: string): Promise<AdminGatewayRecord[]> {
  return request(`/admin/gateways${_tenantQs(tenantId)}`);
}

export function adminListAgentBackends(
  tenantId?: string,
): Promise<AdminAgentBackendRecord[]> {
  return request(`/admin/agent-backends${_tenantQs(tenantId)}`);
}

export function transferResource(
  resource: TransferResource,
  id: string,
  targetTenantId: string,
  confirm: boolean,
): Promise<TransferResponse> {
  return request(`/admin/${resource}/${encodeURIComponent(id)}/transfer`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ target_tenant_id: targetTenantId, confirm }),
  });
}

export interface AdminStartCallRequest {
  tenant_id: string;
  number: string;
  playbook_id: string;
  gateway: string;
  params?: Record<string, string>;
}

export function adminStartCall(req: AdminStartCallRequest): Promise<{
  call_id: string;
  session_id: string;
  greeting: string;
}> {
  return request("/admin/call", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(req),
  });
}

export interface CallWorker {
  worker_id: string;
  worker_name: string;
  version: string;
  dispatch_state: "active" | "draining";
  online: boolean;
  active_calls: number;
  event_loop_lag_ms: number;
  last_seen: number;
  ws_base_url: string;
  started_at: number | null;
}

export function listCallWorkers(): Promise<{ workers: CallWorker[] }> {
  return request("/admin/call-workers");
}

export function setCallWorkerState(
  workerId: string,
  state: "active" | "draining",
): Promise<{ worker_id: string; dispatch_state: string }> {
  return request(`/admin/call-workers/${encodeURIComponent(workerId)}/state`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ state }),
  });
}

// ── Global analytics (cross-tenant) ──

function _adminRangeParams(startDate: string, endDate: string): URLSearchParams {
  return new URLSearchParams({ start_date: startDate, end_date: endDate });
}

export function adminGetDailyTrend(startDate: string, endDate: string): Promise<{ days: DailyTrendItem[] }> {
  return request(`/admin/analytics/daily-trend?${_adminRangeParams(startDate, endDate)}`);
}

export function adminGetTechMetrics(startDate: string, endDate: string): Promise<TechMetrics> {
  return request(`/admin/analytics/tech-metrics?${_adminRangeParams(startDate, endDate)}`);
}

export function adminGetRejectBreakdown(startDate: string, endDate: string): Promise<{ items: RejectBreakdownItem[] }> {
  return request(`/admin/analytics/reject-breakdown?${_adminRangeParams(startDate, endDate)}`);
}

export function adminGetPlaybookDistribution(startDate: string, endDate: string): Promise<{ items: PlaybookDistributionItem[] }> {
  return request(`/admin/analytics/playbook-distribution?${_adminRangeParams(startDate, endDate)}`);
}
